import React, { useState } from "react";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Eye, EyeOff } from "lucide-react";
import AuthApi from "../api/authApi";
import companyLogo from "../assets/images/logo.png";
import { getAuthUser } from "../hooks/useAuthUser";

interface RegistrationPasswordAuthProps {
  children?: React.ReactNode;
  onVerified?: () => void;
}

const RegistrationPasswordAuth: React.FC<RegistrationPasswordAuthProps> = ({
  children,
  onVerified,
}) => {
  const userData = getAuthUser();
  const isAdmin = userData && userData.userRole === "ADMIN";

  const [isVerified, setIsVerified] = useState<boolean>(
    isAdmin || !!sessionStorage.getItem("retreat-registration-password"),
  );
  const [registrationPassword, setRegistrationPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const authApi = new AuthApi();

  const handleVerifyPassword = async () => {
    const gatePassword = registrationPassword.trim();
    if (!gatePassword) {
      toast.error("Please enter the registration password.");
      return;
    }
    setIsVerifying(true);
    try {
      const res: any = await authApi.verifyRegistrationPassword({
        password: gatePassword,
      });
      if (res?.success && res?.valid) {
        sessionStorage.setItem(
          "retreat-registration-password",
          gatePassword,
        );
        setIsVerified(true);
        if (onVerified) onVerified();
        toast.success("Password verified successfully!");
      } else {
        toast.error(res?.msg || "Invalid password.");
      }
    } catch {
      toast.error("Invalid password. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  if (!isVerified) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          background: "linear-gradient(180deg, #f4f7fb 0%, #e8eef6 100%)",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            width: "100%",
            maxWidth: 440,
            p: { xs: 3, sm: 4.5 },
            borderRadius: 3,
            textAlign: "center",
          }}
        >
          <Link to="/">
            <Box
              component="img"
              src={companyLogo}
              alt="LOVESTRONG"
              sx={{ width: 150, mb: 2 }}
            />
          </Link>
          <Typography variant="h5" sx={{ fontWeight: 800, color: "#192f4d" }}>
            New Retreat Registration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
            Please enter the registration password to continue.
          </Typography>
          <TextField
            fullWidth
            placeholder="Registration Password"
            type={showPassword ? "text" : "password"}
            value={registrationPassword}
            onChange={(e) => setRegistrationPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleVerifyPassword();
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            fullWidth
            variant="contained"
            sx={{ mt: 2.5, py: 1.2, bgcolor: "#192f4d", fontWeight: 700 }}
            onClick={handleVerifyPassword}
            disabled={isVerifying}
          >
            {isVerifying ? "Verifying..." : "Confirm"}
          </Button>
        </Paper>
      </Box>
    );
  }

  return <>{children}</>;
};

export default RegistrationPasswordAuth;
